import { useCallback, useEffect, useRef } from 'react';
import { useToast } from '@/hooks/useToast';
// Mesmo motor do tour de Férias (driver.js, visual e desenho do balão) — só reaproveitado, sem mudar Férias.
import { loadDriver } from '@/features/Vacations/components/VacationHelp/tour/loadDriver';
import { resolveTourElement } from '@/features/Vacations/components/VacationHelp/tour/resolveTourElement';
import { renderTourStep } from '@/features/Vacations/components/VacationHelp/tour/renderTourStep';
import type { PassoTourSi } from './siPortalTour.definitions';

interface OpcoesTour {
  /** Chamado se a pessoa foi até o último passo e clicou em Concluir. */
  aoConcluir?: () => void;
  /** Chamado se a pessoa saiu antes do fim (X, Esc, clique fora). */
  aoSair?: () => void;
}

type Alvo = HTMLElement | (() => HTMLElement);

/** Espera os alvos aparecerem (a aba pode estar carregando dados) — até ~2,5s. Passo com `antes`
 * entra mesmo sem alvo agora: o alvo só aparece depois de preparar a tela (resolvido na hora). */
async function esperarAlvos(passos: PassoTourSi[]): Promise<Array<{ passo: PassoTourSi; elemento: Alvo }>> {
  const comAntes = (passo: PassoTourSi): Alvo => () =>
    resolveTourElement(passo.element) ?? (document.querySelector<HTMLElement>('[data-tour="si-ajuda"]') as HTMLElement);
  for (let tentativa = 0; tentativa < 10; tentativa++) {
    const achados = passos.flatMap((passo) => {
      if (passo.antes) return [{ passo, elemento: comAntes(passo) }];
      const elemento = resolveTourElement(passo.element);
      return elemento ? [{ passo, elemento: elemento as Alvo }] : [];
    });
    const semAntes = passos.filter((p) => !p.antes).length;
    const achadosSemAntes = achados.filter((a) => !a.passo.antes).length;
    if (achadosSemAntes > 0 && (tentativa >= 3 || achadosSemAntes === semAntes)) return achados;
    await new Promise((r) => setTimeout(r, 250));
  }
  return [];
}

/** Tour guiado de uma aba do Portal do SI. "Visto" fica no navegador, por pessoa. */
export function useSiPortalTour(email: string | null | undefined) {
  const toast = useToast();
  const instancia = useRef<{ destroy: () => void } | null>(null);
  const chaveVisto = `si-portal-tour-visto:v1:${email ?? 'anon'}`;

  const jaViu = useCallback(() => {
    try {
      return localStorage.getItem(chaveVisto) === 'true';
    } catch {
      return true;
    }
  }, [chaveVisto]);

  const marcarVisto = useCallback(() => {
    try {
      localStorage.setItem(chaveVisto, 'true');
    } catch {
      // sem storage: o ? continua piscando, sem prejuízo
    }
  }, [chaveVisto]);

  const iniciar = useCallback(
    async (passos: PassoTourSi[], opcoes: OpcoesTour = {}) => {
      instancia.current?.destroy();
      const driverModule = await loadDriver();
      if (!driverModule) return;
      const validos = await esperarAlvos(passos);
      if (validos.length === 0) {
        toast.info('Abra esta aba com o conteúdo carregado para ver o tour.');
        opcoes.aoSair?.();
        return;
      }
      marcarVisto();
      let chegouAoFim = false;
      const driverObj = driverModule.driver({
        animate: true,
        duration: 300,
        smoothScroll: true,
        allowClose: true,
        allowKeyboardControl: true,
        allowScroll: true,
        overlayClickBehavior: 'close',
        disableActiveInteraction: true,
        overlayColor: '#161343',
        overlayOpacity: 0.35,
        stagePadding: 8,
        stageRadius: 14,
        popoverOffset: 14,
        popoverClass: 'vacation-tour',
        showProgress: false,
        prevBtnText: '← Voltar',
        nextBtnText: 'Próximo →',
        doneBtnText: 'Concluir ✓',
        closeBtnLabel: 'Sair do tour',
        onNextClick: () => {
          if (!driverObj.hasNextStep()) {
            chegouAoFim = true;
            driverObj.destroy();
            return;
          }
          // Próximo passo precisa preparar a tela (ex.: abrir "Meus equipamentos"): faz e espera renderizar.
          const proximo = validos[(driverObj.getActiveIndex() ?? 0) + 1];
          if (proximo?.passo.antes) {
            proximo.passo.antes();
            setTimeout(() => driverObj.moveNext(), 450);
            return;
          }
          driverObj.moveNext();
        },
        onDestroyed: () => {
          instancia.current = null;
          if (chegouAoFim) opcoes.aoConcluir?.();
          else opcoes.aoSair?.();
        },
        steps: validos.map(({ passo, elemento }, i) => ({
          element: elemento,
          popover: {
            title: passo.title,
            description: renderTourStep(passo, i + 1, validos.length),
            side: passo.side ?? 'bottom',
            align: passo.align ?? 'center',
          },
        })),
      });
      instancia.current = driverObj;
      driverObj.drive();
    },
    [marcarVisto, toast]
  );

  useEffect(() => () => instancia.current?.destroy(), []);

  return { iniciar, jaViu };
}
